import { model as router } from '!/router'
import {
  AuthFormBoxPaddings,
  AuthFormBoxRowGaps,
  AuthFormBoxTemplate,
  ErrorMessage,
  PrimarySubmitButton,
  TextBoxCaption3,
  TextBoxCaption4Secondary,
  TextBoxHeadline2,
} from '@setplex/arcana'
import { useForm } from 'effector-forms'
import { useUnit } from 'effector-react'
import type { JSX } from 'react'
import { type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { AuthTab } from '~/features/change-auth-type'
import { AuthLayout } from '~/widgets/auth'
import { translations, model as verifyModel } from '../model'
import { BackButton } from './BackButton'
import { LockedBlock } from './LockedBlock'
import { VerifyCodeInput } from './VerifyCodeInput'
import { VerifyTextBlock } from './VerifyTextBlock'
import { useCountdown } from './useCountdown'
import { AppTitle } from '~/shared/ui/components/AppTitle'

export function Title(): JSX.Element {
  return <AppTitle t='account_verify' />
}

VerifyPage.displayName = 'VerifyPage'
export function VerifyPage(): JSX.Element {
  const { t } = useTranslation()
  const pending = useUnit(verifyModel.$pendingRegistration)
  const errorKey = useUnit(verifyModel.$viewError)
  const permanentError = useUnit(verifyModel.$permError)
  const locked = useUnit(verifyModel.$locked)
  const lockedError = useUnit(verifyModel.$lockedError)
  const formTranslations = useUnit(translations.$verifyFormTranslation)
  const verifyType = useUnit(router.$search)

  const { submit, eachValid } = useForm(verifyModel.confirmForm)

  const { liveTo, liveResendIn } = useCountdown()

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    submit()
  }

  const isSubmitButtonDisabled =
    pending ||
    !eachValid ||
    liveTo === null ||
    locked ||
    Boolean(permanentError)

  const titleButton = locked
    ? formTranslations.lockedButton
    : formTranslations.verifyButton

  const footerText =
    verifyType?.replace('?=', '') === AuthTab.Phone
      ? formTranslations.verifyPhoneFooter
      : formTranslations.verifyFooter

  return (
    <AuthLayout>
      <AuthFormBoxTemplate
        rowGap={AuthFormBoxRowGaps.SIZE_20}
        padding={AuthFormBoxPaddings.SIZE_140_100}
      >
        {!locked && (
          <form
            onSubmit={onSubmit}
            autoComplete='off'
            noValidate
            style={{ marginBottom: '12px' }}
          >
            <BackButton />
            <TextBoxHeadline2 title={formTranslations.title} />
            <TextBoxCaption3 title={formTranslations.description} />
            <VerifyCodeInput liveTo={liveTo} />
            <PrimarySubmitButton
              title={titleButton}
              disabled={isSubmitButtonDisabled}
              isLoading={pending || locked}
            />
          </form>
        )}
        {locked && <LockedBlock />}
        {!locked && (
          <VerifyTextBlock liveTo={liveTo} liveResendIn={liveResendIn} />
        )}
        {!locked && <TextBoxCaption4Secondary title={footerText} />}
        {errorKey && !lockedError && (
          <ErrorMessage width='100%' title={t(errorKey)} />
        )}
      </AuthFormBoxTemplate>
    </AuthLayout>
  )
}
